// @ts-nocheck
import { useState, useEffect, useMemo, JSXElementConstructor, Key, ReactElement } from "react";
import { SubmissionStoreProvider } from "./models/applications/SubmissionStore";
import { Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { MerchantProvider } from "./pages/applications/tabs/submissions/MerchantContext";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Sidenav from "lib/Sidenav";
import theme from "assets/theme";
import getDynamicRoutes from "routes";
import { useMaterialUIController } from "context";
import OldParentWrapper from "OldParentWrapper";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import CustomDialog from "components/CustomCard";
import { MESSAGES } from "constants/Messages";
import FullPageLoader from "components/Loader/FullPage";
import MDSnackbar from "components/MDSnackbar";
import WarningDialog from "components/WarningDialog";
import MetabaseViewerWrapper from "pages/dashboard/MetabaseViewerWrapper";
import StorageUtils from "utils/StorageUtils";
import { rolePermissionExists } from "utils/CommonUtils";

export default function App() {
  const [controller] = useMaterialUIController();
  const { sidenavColor } = controller;
  const { pathname } = useLocation();
  const [showTokenExpiredAlert, setShowTokenExpiredAlert] = useState(false);
  const [showTenantChangeAlert, setShowTenantChangeAlert] = useState(false);
  const [permissionsReady, setPermissionsReady] = useState(false);
  const [alert, setAlert] = useState(null);
  const [routes, setRoutes] = useState([]);
  const GUARDED_KEYS = new Set(["applications", "application-detail","syndicators-list","brokers-list","offers-list"]);


  const handleClose = () => {
    setShowTokenExpiredAlert(false);
    window.App.state.showTokenExpiredAlert = false;
  };

  const handleConfirm = () => {
    setShowTokenExpiredAlert(false);
    window.App.state.showTokenExpiredAlert = false;
    window.location.reload();
  };

  const handleCloseTenantChangeAlert = () => {
    setShowTenantChangeAlert(false);
    window.App.state.showTenantChangeAlert = false;
  };

  const handleConfirmTenantChangeAlert = () => {
    setShowTenantChangeAlert(false);
    window.App.state.showTenantChangeAlert = false;
    window.location.reload();
  };

  useEffect(() => {
    if (
      (pathname && pathname?.includes("deal_applications")) ||
      pathname?.includes("reports") ||
      pathname?.includes("syndicatorsdashboard") ||
      pathname?.includes("metabase") ||
      pathname?.includes("offers") ||
      pathname?.includes("syndicators") ||
      pathname?.includes("brokers")

    ) {
      document.documentElement.style.setProperty("font-size", "16px", "important");
    }
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  useEffect(() => {
    async function getAllRoutes() {
      const routes = await getDynamicRoutes();
      setRoutes(routes);
    }
    getAllRoutes();
  }, [pathname]);

  useEffect(() => {
    const checkPermissions = setInterval(() => {
      if (window.rolePermission) {
        setPermissionsReady(true);
        clearInterval(checkPermissions);
      }
    }, 100);

    const failSafe = setTimeout(() => {
      clearInterval(checkPermissions);
      if (!permissionsReady) {
        console.warn("rolePermission not detected within 10s — rendering anyway.");
        setPermissionsReady(true);
      }
    }, 5000);

    return () => {
      clearInterval(checkPermissions);
      clearTimeout(failSafe);
    };
  }, []);

  useEffect(() => {
    const pollInterval = setInterval(() => {
      const currentAlertState = window?.App?.state?.showTokenExpiredAlert;
      if (currentAlertState !== showTokenExpiredAlert) {
        setShowTokenExpiredAlert(currentAlertState);
      }
    }, 1000);
    return () => clearInterval(pollInterval);
  }, [showTokenExpiredAlert]);

  useEffect(() => {
    const tenantChangeInterval = setInterval(() => {
      const currentTenantChangeState = window?.App?.state?.showTenantChangeAlert;
      if (currentTenantChangeState !== showTenantChangeAlert) {
        setShowTenantChangeAlert(currentTenantChangeState);
      }
    }, 1000);

    return () => clearInterval(tenantChangeInterval);
  }, [showTenantChangeAlert]);


  const hasPermission = (permission?: { action: string; resource: string }): boolean => {
    if (!permission) return true;
    try {
      return rolePermissionExists(
        window?.rolePermission?.permissions || [],
        permission.action,
        permission.resource
      );
    } catch {
      return false;
    }
  };
  
  const isFeatureEnabled = (feature?: string): boolean => {
    if (!feature) return true;
    try {
      return StorageUtils.isFeatureEnabled(feature);
    } catch {
      return false;
    }
  };
  
  const isRouteAllowedKeyed = (route: any): boolean => {
    if (!route) return false;
    if (!GUARDED_KEYS.has(route.key)) return true;
    const { permission, feature, additionalCheck } = route;
    if ((permission && !hasPermission(permission)) || (feature && !isFeatureEnabled(feature))) return false;
    if (typeof additionalCheck === "function" && !additionalCheck()) return false;
    return true;
  };
    
  const getRoutes = (allRoutes: any[]): any =>
    allRoutes.map((route: any) => {
      if (route?.collapse) {
        const children = route.collapse.filter(isRouteAllowedKeyed);
        return getRoutes(children);
      }
  
      if (route?.route && route?.component && isRouteAllowedKeyed(route)) {
        return <Route path={route.route} element={route.component} key={route.key} />;
      }
  
      return null;
    });


  const SyndicatorRedirectWrapper = () => {
    const { id } = useParams();
    console.log("SyndicatorRedirectWrapper, navigating to old UI for ID:", id);
    if (!id) {
      return <Navigate to="/synd/details" replace />;
    }

    return <OldParentWrapper routeProps={{ match: { params: { partyId: id } } }} />;
  };

  const BrokerRedirectWrapper = () => {
    const { id } = useParams();
    console.log("BrokerRedirectWrapper, navigating to old UI for ID:", id);
    if (!id) {
      return <Navigate to="/broker/details" replace />;
    }

    return <OldParentWrapper routeProps={{ match: { params: { partyId: id } } }} />;
  };

  if (!permissionsReady) {
    return (
      <div style={{ textAlign: "center", marginTop: "20%" }}>
        <FullPageLoader />
      </div>
    );
  }

  return (
    <DndProvider backend={HTML5Backend}>
      <SubmissionStoreProvider>
        <MerchantProvider>
          <ThemeProvider theme={theme}>
            <CssBaseline />
            <Sidenav
              color={sidenavColor}
              brandName={localStorage.getItem("tenantName")}
              routes={routes}
              alert={alert}
              setAlert={setAlert}
            />

            <Routes>
              {getRoutes(routes)}
              <Route path="/metabase/dashboard/:id" element={<MetabaseViewerWrapper />} />

              <Route path="/synd/details/:id" element={<SyndicatorRedirectWrapper />} />
              <Route path="/broker/details/:id" element={<BrokerRedirectWrapper />} />

              <Route
                path="/dashboard"
                element={
                  <OldParentWrapper>
                    <Navigate to="/dashboard" replace />
                  </OldParentWrapper>
                }
              />

              <Route
                path="/deals"
                element={
                  <OldParentWrapper>
                    <Navigate to="/deals" replace />
                  </OldParentWrapper>
                }
              />
              <Route
                path="/deal_transactions"
                element={
                  <OldParentWrapper>
                    <Navigate to="/deal_transactions" replace />
                  </OldParentWrapper>
                }
              />

              <Route
                path="/merchants"
                element={
                  <OldParentWrapper>
                    <Navigate to="/merchants" replace />
                  </OldParentWrapper>
                }
              />

              <Route
                path="/meetings"
                element={
                  <OldParentWrapper>
                    <Navigate to="/meetings" replace />
                  </OldParentWrapper>
                }
              />

              <Route
                path="/syndicator"
                element={
                  <OldParentWrapper>
                    <Navigate to="/syndicator" replace />
                  </OldParentWrapper>
                }
              />

              <Route
                path="/settings"
                element={
                  <OldParentWrapper>
                    <Navigate to="/settings" replace />
                  </OldParentWrapper>
                }
              />

              <Route
                path="/"
                element={
                  <OldParentWrapper>
                    <Navigate to="/deals" replace />
                  </OldParentWrapper>
                }
              />
            </Routes>

            {showTokenExpiredAlert && (
              <WarningDialog
                open={showTokenExpiredAlert}
                message={MESSAGES.TOKEN_EXPIRED}
                onConfirm={handleConfirm}
                type="warning"
                showActions={true}
              />
            )}

            {showTenantChangeAlert && (
              <WarningDialog
                open={showTenantChangeAlert}
                message={MESSAGES.SIGN_IN_CONTEXT_CHANGE}
                onConfirm={handleConfirmTenantChangeAlert}
                type="warning"
                showActions={true}
              />
            )}

            {alert && (
              <MDSnackbar
                color={alert?.type === "success" ? "success" : "error"}
                icon="notifications"
                title={alert?.type === "success" ? "Success" : "Error"}
                content={alert?.message}
                dateTime=""
                autoHideDuration={alert?.timeOut || 3000}
                open={alert !== null}
                close={() => setAlert(null)}
              />
            )}
          </ThemeProvider>
        </MerchantProvider>
      </SubmissionStoreProvider>
    </DndProvider>
  );
}
